
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { NavBar } from '@/components/navbar';
import { useLogoUpload } from './logo/useLogoUpload';
import { BusinessDetailsHeader } from './businessDetails/BusinessDetailsHeader';
import { TabsSection } from './businessDetails/TabsSection';
import { useBusinessForm } from './businessDetails/hooks/useBusinessForm';
import MobileFooterButtons from '@/components/portfolio/settings/MobileFooterButtons';

export const EditBusinessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessId } = useParams<{ businessId: string }>();
  const { toast } = useToast();
  
  // Get the activeTab from location state, default to 'business-info'
  const initialActiveTab = location.state?.activeTab || 'business-info';
  const [activeTab, setActiveTab] = useState<string>(initialActiveTab);
  
  const {
    business,
    formData,
    formChanged,
    displayNameManuallyEdited,
    calendarOpen,
    setCalendarOpen,
    date,
    handleInputChange,
    handleSelectChange,
    handleDateSelect,
    handleCancel,
    handleSave
  } = useBusinessForm(businessId);
  
  // Set active tab when coming from the logo click
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);
  
  // Redirect if no business found
  useEffect(() => {
    if (!business && businessId) {
      toast({
        title: "Business not found",
        description: "The business you are trying to edit does not exist.",
        variant: "destructive"
      });
      navigate('/');
    }
  }, [business, businessId, navigate, toast]);
  
  // Logo upload state and functions
  const {
    logoPreview,
    file,
    uploadError,
    previewTab,
    setPreviewTab,
    logoLayout,
    setLogoLayout,
    handleFileChange,
    handleSaveLogo,
    handleReset,
    handleRemoveLogo
  } = useLogoUpload(business || { id: businessId || "" } as any, true);
  
  // Track if logo has been changed
  const [logoChanged, setLogoChanged] = useState(false);
  
  // Update logo changed state when logo preview changes
  useEffect(() => {
    if (business) {
      const hasLogoChanged = (logoPreview !== business.logoUrl) || 
        (logoLayout !== (business.logoLayout === 'full' ? 'square' : (business.logoLayout || 'square')));
      
      setLogoChanged(hasLogoChanged);
      
      // Debug
      console.log('Logo changed detection:', {
        logoPreview,
        businessLogoUrl: business.logoUrl,
        logoLayout,
        businessLogoLayout: business.logoLayout,
        hasLogoChanged
      });
    }
  }, [logoPreview, logoLayout, business]);
  
  // Handle back navigation - Go back to gocampaign
  const handleBack = () => {
    if (formChanged || logoChanged) {
      // If there are changes, ask the user to confirm before discarding
      const confirmed = handleCancel();
      if (confirmed) {
        navigate(`/business/${businessId}/gocampaign`);
      }
    } else {
      navigate(`/business/${businessId}/gocampaign`);
    }
  };
  
  // Handle save business details - Save and go back to gocampaign
  const handleSaveBusiness = () => {
    // First save the business details
    const success = handleSave();
    
    // Then save the logo if it's been changed
    if (success && logoChanged) {
      handleSaveLogo();
    }
    
    if (success) {
      toast({
        title: "Success",
        description: "Business details updated successfully.",
      });
      
      // Navigate back to gocampaign after save
      navigate(`/business/${businessId}/gocampaign`);
    }
  };
  
  // Prepare logoProps to pass to TabsSection
  const logoProps = {
    logoPreview,
    uploadError,
    previewTab,
    setPreviewTab,
    logoLayout, 
    setLogoLayout,
    handleFileChange,
    handleSaveLogo,
    handleReset,
    handleRemoveLogo,
    setLogoChanged
  };
  
  if (!business) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    ); // Loading state
  }

  // Calculate if any changes have been made (form or logo)
  const hasChanges = formChanged || logoChanged;
  
  // Debug
  console.log('Form and logo status:', { formChanged, logoChanged, hasChanges });

  return (
    <div className="min-h-screen bg-brand-light flex flex-col">
      <NavBar businessName={business.name} />
      
      <main className="flex-1 max-w-screen-xl mx-auto px-4 py-8 w-full animate-fade-in pb-24">
        <BusinessDetailsHeader 
          handleBack={handleBack}
          handleSave={handleSaveBusiness}
          showButtons={false}
        />
        
        <TabsSection 
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          date={date}
          calendarOpen={calendarOpen}
          setCalendarOpen={setCalendarOpen}
          handleDateSelect={handleDateSelect}
          displayNameManuallyEdited={displayNameManuallyEdited}
          business={business}
          logoProps={logoProps}
        />
      </main>
      
      {/* Mobile footer buttons - only show when there are changes */}
      <MobileFooterButtons
        formEdited={hasChanges}
        handleCancel={handleBack}
        handleSave={handleSaveBusiness}
      />
    </div>
  );
};

export default EditBusinessPage;
